import { useEffect, useState, useRef } from 'react'
import useWorksCount from '@pc/components/Works/Panel/hooks/useWorksCount'
import SearchControlBox from '@pc/components/Works/Panel/shared/SearchControlBox'
import type { SearchParamsAttributes, Prefecture, RawLineCompany, Line, Station } from '@pc/components/Works/Panel/types'
import { buildDynamicSearchApiUrl } from '@pc/components/Works/Panel/utils/workSearchApi'
import AreaPanelButton from './AreaSelectModal/AreaPanelButton'
import PanelButtonPopupIcon from './PanelButtonPopupIcon'
import ModalCloseButton from './shared/ModalCloseButton'
import ModalFooter from './shared/ModalFooter'
import ModalHeader from './shared/ModalHeader'
import ModalInner from './shared/ModalInner'
import ModalListWrapper from './shared/ModalListWrapper'
import ModalTitle from './shared/ModalTitle'
import SelectModalDialog from './shared/SelectModalDialog'
import LineCompanyList from './StationSelectModal/LineCompanyList'
import type { MouseEvent } from 'react'

interface PropsType {
  params: SearchParamsAttributes
  prefecture: Prefecture
  rawLineCompanies: RawLineCompany[]
  lines: Line[]
  stations: Station[]
}

type MappedLines = Record<string, Line[]>

const GUIDE_AREA_URL = '/guide_area.htm'

const isKey = <T extends object>(x: T, k: PropertyKey): k is keyof T => {
  return k in x
}

const StationSelectModal = (props: PropsType) => {
  const { params, prefecture, rawLineCompanies: rawLineCompaniesData, lines: linesData, stations: stationsData } = props

  const buildInitialStationState = () => {
    const mappedLines: MappedLines = rawLineCompaniesData.reduce((result, lineCompany) => {
      const value = linesData.filter((line) => line.attributes.line_company_id === lineCompany.attributes.id)
      return { ...result, [lineCompany.id]: value }
    }, {})

    const mappedStations = linesData.reduce((result, line) => {
      const value = stationsData.filter((station) => {
        const lineIds = station.relationships.lines.data.map((stationLine) => stationLine.id)
        return lineIds.includes(line.id)
      })
      return { ...result, [line.id]: value }
    }, {})

    // マイナー路線しか持たない鉄道会社が上の方に来てしまうことを防ぐために鉄道会社を路線数が多い順に並べかえている
    const lineCompanyItems = (lineCompanyId: string) => {
      return isKey(mappedLines, lineCompanyId) ? mappedLines[lineCompanyId] : []
    }
    const lineCompanies = [...rawLineCompaniesData].sort((a, b) => lineCompanyItems(b.id).length - lineCompanyItems(a.id).length)

    return {
      lineCompanies,
      mappedLines,
      mappedStations
    }
  }

  const { lineCompanies, mappedLines, mappedStations } = buildInitialStationState()

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [selectedStationIds, setSelectedStationIds] = useState<string[]>([])
  const { worksCount, updateCountFromParams } = useWorksCount()
  const renderFlgRef = useRef(false)

  const toggleModal = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setModalIsOpen(!modalIsOpen)
  }

  const closeModal = () => {
    setModalIsOpen(false)
  }

  const changeStationIds = (stationIds: string[]) => {
    setSelectedStationIds(stationIds)
  }

  const clearState = () => {
    setSelectedStationIds([])
  }

  const mergedParams = () => {
    // 駅・路線系の検索の場合は地域系の条件をリセットする仕様になっている
    // 検索パネルの仕様上、lineを変更することができず情報を保持していると検索結果がおかしくなる可能性があるためリセットする
    return { ...params, station_ids: selectedStationIds, lines_ids: [], area_codes: [], city_ids: [], town_ids: [] }
  }

  // ブランドを1件に絞っている文脈では、都道府県を選び直してもブランド条件を引き継げるようにする。
  // GUIDE_AREA_URL(/guide_area.htm)は/prefecturesへの301でクエリが落ちるため、ブランド時は直接/prefecturesへ付与する
  const guideAreaUrl = () => {
    const brandIds = params.brand_ids
    if (brandIds?.length === 1) {
      return `/prefectures?brand_id=${brandIds[0]}`
    }
    return GUIDE_AREA_URL
  }

  useEffect(() => {
    if (renderFlgRef.current) {
      const mergedParams = { ...params, station_ids: selectedStationIds, lines_ids: [], area_codes: [], city_ids: [], town_ids: [] }
      updateCountFromParams(mergedParams)
    } else {
      renderFlgRef.current = true
    }
  }, [selectedStationIds, params, updateCountFromParams])

  useEffect(() => {
    const preSelectedStationIds: string[] = params.station_ids ?? []
    setSelectedStationIds(preSelectedStationIds)
  }, [params.station_ids])

  return (
    <>
      <AreaPanelButton onClick={toggleModal}>
        路線・駅変更
        <PanelButtonPopupIcon />
      </AreaPanelButton>
      <SelectModalDialog isOpen={modalIsOpen} onRequestClose={closeModal}>
        <ModalInner>
          <ModalCloseButton onClick={toggleModal} />
          <ModalHeader>
            <ModalTitle>{prefecture.attributes.name}内の路線・駅変更</ModalTitle>
            <a href={guideAreaUrl()}>都道府県を変更</a>
          </ModalHeader>
          <ModalListWrapper>
            <LineCompanyList
              lineCompanies={lineCompanies}
              mappedLines={mappedLines}
              mappedStations={mappedStations}
              selectedStationIds={selectedStationIds}
              changeStationIds={changeStationIds}
            />
          </ModalListWrapper>
          <ModalFooter>
            <SearchControlBox searchUrl={buildDynamicSearchApiUrl(mergedParams())} onClickClearButton={clearState} worksCount={worksCount} />
          </ModalFooter>
        </ModalInner>
      </SelectModalDialog>
    </>
  )
}

export default StationSelectModal
